import { useEffect, useRef, useState } from "react";
import { animate, useInView } from "framer-motion";
import { Reveal, SectionHeading } from "./Reveal";
import { PILLARS, STANDARDS } from "../data/content";

const Counter = ({ to, suffix }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const [val, setVal] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, to, {
      duration: 1.8,
      ease: [0.22, 1, 0.36, 1],
      onUpdate: (v) => setVal(Math.round(v)),
    });
    return () => controls.stop();
  }, [inView, to]);

  return (
    <span ref={ref} className="font-display text-5xl sm:text-6xl font-bold text-gold-metallic">
      {val}
      {suffix}
    </span>
  );
};

export const About = () => (
  <section id="about" data-testid="about-section" className="noise-overlay relative bg-navy-950 py-24 lg:py-32">
    <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
      <div className="grid grid-cols-1 items-start gap-14 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <SectionHeading
            overline="Who We Are"
            testId="about-heading"
            title={
              <>
                A consultancy built for how <span className="text-gold-metallic italic">modern business</span> actually works
              </>
            }
          />
          <Reveal delay={0.22}>
            <p className="mt-6 text-base sm:text-lg leading-relaxed text-slate-400">
              Faithh Business Consultancy is a UAE-based business consultancy and digital solutions
              company. We exist for one reason: to take the weight of compliance, technology and
              marketing off your shoulders — so you can focus on running and growing your business.
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <p className="mt-4 text-base sm:text-lg leading-relaxed text-slate-400">
              From VAT and corporate tax filing to websites, apps, advertising and brand identity,
              we bring every essential service under one roof, delivered with the polish and
              professionalism your business deserves.
            </p>
          </Reveal>

          <div className="mt-14 divide-y divide-white/5 border-y border-white/5">
            {PILLARS.map((pillar, i) => (
              <Reveal key={pillar.number} delay={0.08 * i}>
                <div className="group flex items-start gap-6 py-7 transition-colors duration-500 hover:bg-white/[0.02] sm:gap-10" data-testid={`pillar-${pillar.number}`}>
                  <span className="font-mono text-sm text-gold/70 pt-2">{pillar.number}</span>
                  <div>
                    <h3 className="font-display text-2xl sm:text-3xl font-semibold text-slate-100 transition-colors duration-400 group-hover:text-gold-soft">
                      {pillar.title}
                    </h3>
                    <p className="mt-2 max-w-xl text-sm sm:text-base leading-relaxed text-slate-400">{pillar.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        <div className="lg:col-span-5">
          <Reveal delay={0.2} className="lg:sticky lg:top-32">
            <div className="sheen-border relative overflow-hidden rounded-[1.75rem] border border-gold/20">
              <img
                src="https://images.pexels.com/photos/36712838/pexels-photo-36712838.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Business consultation in progress"
                className="h-[320px] w-full object-cover sm:h-[420px]"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-navy-950/80 via-transparent to-transparent" />
              <div className="absolute bottom-5 left-5 right-5">
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold-soft">Faithh Business Consultancy</p>
                <p className="mt-1 font-display text-xl font-semibold text-slate-50">One partner. Every essential service.</p>
              </div>
            </div>
          </Reveal>
        </div>
      </div>

      <div className="mt-24 grid grid-cols-2 gap-x-6 gap-y-12 border-t border-gold/10 pt-14 lg:grid-cols-4" data-testid="standards-strip">
        {STANDARDS.map((s, i) => (
          <Reveal key={s.label} delay={0.08 * i} className="text-center lg:text-left">
            <Counter to={s.value} suffix={s.suffix} />
            <p className="mt-3 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">{s.label}</p>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);
